import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import api from "utils/api";
import { ANALYTICS_EVENTS, trackEvent } from "utils/analytics";
import { useLatest } from "hooks/useLatest";
import type { PlanSelectionArea } from "../../utils/types";
import type {
  CreateAreaArgs,
  SetAreaModeArgs,
  ToggleAreaFlagArgs,
  UpdateAreaGeometryArgs,
  UseSelectionAreasCrudArgs,
  UseSelectionAreasCrudResult,
} from "./types";

export const useSelectionAreasCrud = ({
  floorPlanId,
  areas,
  addFloorPlanSelectionArea,
  updateFloorPlanSelectionArea,
  deleteFloorPlanSelectionArea,
}: UseSelectionAreasCrudArgs): UseSelectionAreasCrudResult => {
  const { t } = useTranslation();

  const [isCreatingArea, setIsCreatingArea] = useState(false);

  const floorPlanIdLatest = useLatest(floorPlanId);

  const createArea = useCallback(
    async ({ pageId, pageIndex, points }: CreateAreaArgs) => {
      const targetFloorPlanId = floorPlanIdLatest.current;
      if (targetFloorPlanId === undefined) return null;

      setIsCreatingArea(true);
      try {
        const created = await api.createQuantitySurveyorSelectionArea({
          pageId,
          points,
          appliesToElements: true,
          appliesToRoutes: true,
          mode: "exclude",
        });
        trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_SELECTION_AREA_CREATED);
        const area: PlanSelectionArea = {
          id: created.id,
          pageIndex,
          points: created.points,
          appliesToElements: created.appliesToElements,
          appliesToRoutes: created.appliesToRoutes,
          mode: created.mode,
        };
        addFloorPlanSelectionArea({ floorPlanId: targetFloorPlanId, area });
        return area;
      } catch {
        toast.error(t("smartSurveyor.selectionAreas.createFailed"));
        return null;
      } finally {
        setIsCreatingArea(false);
      }
    },
    [floorPlanIdLatest, addFloorPlanSelectionArea, t],
  );

  // TODO: Save shape edits with Save/Delete buttons, like element, route and room creation, instead of on every drag
  const updateAreaGeometry = useCallback(
    async ({ areaId, points }: UpdateAreaGeometryArgs) => {
      const targetFloorPlanId = floorPlanIdLatest.current;
      if (targetFloorPlanId === undefined) return;
      try {
        await api.updateQuantitySurveyorSelectionArea(areaId, { points });
        trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_SELECTION_AREA_CHANGED, {
          change: "shape",
        });
        updateFloorPlanSelectionArea({
          floorPlanId: targetFloorPlanId,
          areaId,
          changes: { points },
        });
      } catch {
        toast.error(t("smartSurveyor.selectionAreas.updateFailed"));
      }
    },
    [floorPlanIdLatest, updateFloorPlanSelectionArea, t],
  );

  const toggleAreaFlag = useCallback(
    ({ areaId, flag }: ToggleAreaFlagArgs) => {
      if (floorPlanId === undefined) return;
      const area = areas.find((candidate) => candidate.id === areaId);
      if (!area) return;

      const nextValue = !area[flag];
      updateFloorPlanSelectionArea({
        floorPlanId,
        areaId,
        changes: { [flag]: nextValue },
      });
      api
        .updateQuantitySurveyorSelectionArea(areaId, { [flag]: nextValue })
        .then(() =>
          trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_SELECTION_AREA_CHANGED, {
            change: flag,
          }),
        )
        .catch(() => {
          updateFloorPlanSelectionArea({
            floorPlanId,
            areaId,
            changes: { [flag]: area[flag] },
          });
          toast.error(t("smartSurveyor.selectionAreas.updateFailed"));
        });
    },
    [floorPlanId, areas, updateFloorPlanSelectionArea, t],
  );

  const setAreaMode = useCallback(
    ({ areaId, mode }: SetAreaModeArgs) => {
      if (floorPlanId === undefined) return;
      const area = areas.find((candidate) => candidate.id === areaId);
      if (!area || area.mode === mode) return;

      updateFloorPlanSelectionArea({
        floorPlanId,
        areaId,
        changes: { mode },
      });
      api
        .updateQuantitySurveyorSelectionArea(areaId, { mode })
        .then(() =>
          trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_SELECTION_AREA_CHANGED, {
            change: "mode",
          }),
        )
        .catch(() => {
          updateFloorPlanSelectionArea({
            floorPlanId,
            areaId,
            changes: { mode: area.mode },
          });
          toast.error(t("smartSurveyor.selectionAreas.updateFailed"));
        });
    },
    [floorPlanId, areas, updateFloorPlanSelectionArea, t],
  );

  const deleteArea = useCallback(
    async (areaId: number) => {
      const targetFloorPlanId = floorPlanIdLatest.current;
      if (targetFloorPlanId === undefined) return false;
      try {
        await api.deleteQuantitySurveyorSelectionArea(areaId);
        trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_SELECTION_AREA_DELETED);
        deleteFloorPlanSelectionArea({
          floorPlanId: targetFloorPlanId,
          areaId,
        });
        return true;
      } catch {
        toast.error(t("smartSurveyor.selectionAreas.deleteFailed"));
        return false;
      }
    },
    [floorPlanIdLatest, deleteFloorPlanSelectionArea, t],
  );

  return {
    isCreatingArea,
    createArea,
    updateAreaGeometry,
    toggleAreaFlag,
    setAreaMode,
    deleteArea,
  };
};
